import React, { CSSProperties, useMemo, forwardRef } from "react";
import { Tag as PrimeTag, TagProps as PrimeTagProps } from "primereact/tag";

import { getColorBaseTokenValue, type ColorBaseToken } from "../../design-tokens";
import { mergeClassNames, renderMaterialIcon } from "../../design-tokens/utils";
import styles from "./Tag.module.css";

export type TagSeverity =
  | "success"
  | "successInverse"
  | "warning"
  | "warningInverse"
  | "error"
  | "errorInverse"
  | "neutral"
  | "primary"
  | "secondary";

type SeverityPalette = {
  background: ColorBaseToken;
  text: ColorBaseToken;
};

type TagCssVariables = CSSProperties & {
  "--ds-tag-bg": string;
  "--ds-tag-color": string;
};

const SEVERITY_TOKENS: Record<TagSeverity, SeverityPalette> = {
  success: {
    background: "color.feedback.success.900",
    text: "color.feedback.success.100"
  },
  successInverse: {
    background: "color.feedback.success.100",
    text: "color.feedback.success.700"
  },
  warning: {
    background: "color.feedback.warning.900",
    text: "color.feedback.warning.100"
  },
  warningInverse: {
    background: "color.feedback.warning.100",
    text: "color.feedback.warning.700"
  },
  error: {
    background: "color.feedback.danger.900",
    text: "color.feedback.danger.100"
  },
  errorInverse: {
    background: "color.feedback.danger.100",
    text: "color.feedback.danger.700"
  },
  neutral: {
    background: "color.neutral.100",
    text: "color.neutral.700"
  },
  primary: {
    background: "color.primary.100",
    text: "color.primary.700"
  },
  secondary: {
    background: "color.secondary.100",
    text: "color.secondary.700"
  }
};

export interface TagProps
  extends Omit<PrimeTagProps, "value" | "severity" | "icon" | "rounded" | "pt"> {
  label: string;
  severity: TagSeverity;
  icon?: string;
}

export const Tag = forwardRef<HTMLDivElement, TagProps>(
  ({ label, severity, icon, className, style, ...rest }, ref) => {
    const palette = SEVERITY_TOKENS[severity];

    const styleVariables = useMemo<TagCssVariables>(() => {
      const base: TagCssVariables = {
        "--ds-tag-bg": getColorBaseTokenValue(palette.background),
        "--ds-tag-color": getColorBaseTokenValue(palette.text)
      };

      return style ? { ...base, ...style } : base;
    }, [palette, style]);

    const iconElement = icon ? renderMaterialIcon(icon, "icon.usage.listing", "p-tag-icon") : undefined;

    return (
      <div ref={ref} style={{ display: "inline-flex" }}>
        <PrimeTag
          {...rest}
          icon={iconElement}
          className={mergeClassNames(styles.root, className)}
          style={styleVariables}
          value={label}
        />
      </div>
    );
  }
);

Tag.displayName = "Tag";

export default Tag;
